import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { getMemberEvents } from '@/lib/api';
import BookingModal from '@/components/BookingModal';
import AuthModal from '@/components/AuthModal';
import BunchhLogo from '@/components/BunchhLogo';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Users, LogOut, ArrowLeft, Loader2 } from 'lucide-react';

const EVENT_PLACEHOLDER = 'https://images.unsplash.com/photo-1529543544282-ea669407fca3?w=800&q=80';

const GRADIENTS = [
  'linear-gradient(135deg,#3D1A08 0%,#6B2810 100%)',
  'linear-gradient(135deg,#1A2515 0%,#2C3D1A 100%)',
  'linear-gradient(135deg,#1F1A2A 0%,#2E2040 100%)',
  'linear-gradient(135deg,#2A1F0A 0%,#3D2E10 100%)',
  'linear-gradient(135deg,#1F2520 0%,#2A352C 100%)',
  'linear-gradient(135deg,#201A20 0%,#3A2A38 100%)',
];

export default function MembersPortal() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCity, setActiveCity] = useState('Mumbai');
  const [showBooking, setShowBooking] = useState(null);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      setShowAuth(true);
    }
  }, [authLoading, user]);

  useEffect(() => {
    if (user) fetchEvents();
  }, [user]);

  const fetchEvents = async () => {
    try {
      const res = await getMemberEvents();
      setEvents(res.data);
    } catch {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const cities = ['Mumbai'];
  const filtered = events.filter(e => e.city === 'Mumbai');

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Loader2 size={32} className="animate-spin" color="var(--coral)" />
      </div>
    );
  }

  if (!user && showAuth) {
    return <AuthModal onClose={() => { setShowAuth(false); navigate('/'); }} />;
  }

  if (!user) return null;

  return (
    <div data-testid="members-portal">
      {/* Nav */}
      <nav className="bunchh-nav scrolled" data-testid="members-nav">
        <div className="nav-logo" onClick={() => navigate('/')}>
          <BunchhLogo height={28} />
        </div>
        <div className="nav-btns">
          {user.role === 'admin' && (
            <button className="btn-ghost btn-sm" onClick={() => navigate('/admin')} data-testid="members-admin-btn">Admin</button>
          )}
          <button className="btn-ghost btn-sm" onClick={handleLogout} data-testid="members-logout-btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <LogOut size={14} /> Logout
          </button>
        </div>
      </nav>

      {/* Header */}
      <div className="members-header" data-testid="members-header">
        <div className="members-greeting">
          Welcome back.<br /><em>Here's what's coming up.</em>
        </div>
        <p className="members-sub">Book your spot — we'll WhatsApp you the details before the plan.</p>
        <div className="city-filter" data-testid="city-filter">
          {cities.map(c => (
            <button
              key={c}
              className={`city-pill ${activeCity === c ? 'active' : ''}`}
              onClick={() => setActiveCity(c)}
              data-testid={`city-pill-${c.toLowerCase()}`}
            >{c}</button>
          ))}
        </div>
      </div>

      {/* Events Grid */}
      <div className="members-grid" data-testid="members-grid">
        {loading ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60 }}>
            <Loader2 size={32} className="animate-spin" color="var(--coral)" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>No events yet</p>
            <p style={{ fontSize: 14 }}>Check back soon — new plans are added regularly.</p>
          </div>
        ) : (
          filtered.map((event, idx) => (
            <div className="event-card" key={event.id} data-testid={`event-card-${event.id}`}>
              <div
                className="event-card-img"
                style={{
                  backgroundImage: event.image_url ? `url(${event.image_url})` : 'none',
                  background: event.image_url ? undefined : GRADIENTS[idx % GRADIENTS.length]
                }}
              >
                <div className="event-card-badges">
                  <span className="event-badge">{event.event_type}</span>
                  <span className="event-badge">{event.city}</span>
                </div>
              </div>
              <div className="event-card-body">
                <div className="event-card-title">{event.title}</div>
                {event.vibe && <div className="event-card-vibe">{event.vibe}</div>}
                <div className="event-card-meta">
                  {event.date && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={13} /> {event.date}</span>}
                  {event.time && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={13} /> {event.time}</span>}
                  {event.city && <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {event.city}</span>}
                </div>
                <div className="event-card-footer">
                  <div>
                    <div className="event-price">{'\u20B9'}{event.price}</div>
                    <div className="event-spots">
                      {event.spots_left > 0 ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Users size={12} /> {event.spots_left} spots left</span>
                      ) : (
                        <span style={{ color: 'var(--coral)' }}>Fully booked</span>
                      )}
                    </div>
                  </div>
                  {event.user_paid ? (
                    <span style={{ background: '#dcfce7', color: '#166534', padding: '8px 20px', borderRadius: 100, fontSize: 13, fontWeight: 700 }}>Confirmed</span>
                  ) : event.spots_left > 0 ? (
                    <button className="btn-coral btn-sm" onClick={() => setShowBooking(event)} data-testid={`book-btn-${event.id}`}>
                      Book spot
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Booking Modal */}
      {showBooking && (
        <BookingModal
          event={showBooking}
          onClose={() => setShowBooking(null)}
          onSuccess={() => { setShowBooking(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}
